// src/pages/EventDetailPage.js
// Updated: 06/20/2026

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/common/layout/Layout';
import { useAuth } from '../context/AuthContext';
import { useFamily } from '../context/FamilyContext';
import { db } from '../firebase/config';
import { doc, onSnapshot, collection, query, orderBy, deleteDoc, where, getDocs } from 'firebase/firestore';
import { format } from 'date-fns';
import { FaCalendarAlt, FaMapMarkerAlt, FaClock, FaTshirt, FaClipboardList, FaGift, FaPoll, FaPencilAlt, FaTrash, FaInfoCircle, FaTicketAlt } from 'react-icons/fa';
import QRCode from 'react-qr-code';
import toast from 'react-hot-toast';
import Spinner from '../components/common/Spinner';
import AddItineraryModal from '../components/dashboard/AddItineraryModal';
import ManageMerchModal from '../components/dashboard/ManageMerchModal';
import CreatePollModal from '../components/dashboard/CreatePollModal';
import PhotoGallery from '../components/common/PhotoGallery';
import Poll from '../components/event/Poll';
import ConfirmationModal from '../components/common/ConfirmationModal';

const EventDetailPage = () => {
  const { eventId } = useParams();
  const { userData } = useAuth();
  const { familyData, isHost: isFamilyHost } = useFamily();
  const [event, setEvent] = useState(null);
  const [itinerary, setItinerary] = useState([]);
  const [merchandise, setMerchandise] = useState([]);
  const [polls, setPolls] = useState([]);
  const [userRegistration, setUserRegistration] = useState(null); // User's ticket
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ type: null, data: null });

  const eventGalleryPath = familyData && eventId ? `families/${familyData.id}/events/${eventId}/gallery` : null;

  // Check if current user is specifically assigned to manage this event
  const isEventHost = event?.eventHosts?.includes(userData?.uid);
  const canManage = isFamilyHost || isEventHost; 

  useEffect(() => {
    if (!familyData || !eventId) { setLoading(false); return; }
    setLoading(true);
    
    const eventDocRef = doc(db, `families/${familyData.id}/events/${eventId}`);
    
    // Fetch Event
    const unsubEvent = onSnapshot(eventDocRef, (doc) => { 
        if (doc.exists()) setEvent({ id: doc.id, ...doc.data() }); 
        else { console.error("Event not found!"); setEvent(null); } 
    }, (error) => { console.error("Error fetching event:", error); setLoading(false); });
    
    // Fetch Subcollections
    const unsubItinerary = onSnapshot(query(collection(eventDocRef, 'itinerary'), orderBy('date')), (snap) => { setItinerary(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); }, (error) => { console.error("Error fetching itinerary:", error); setLoading(false); });
    const unsubMerch = onSnapshot(query(collection(eventDocRef, 'merchandise'), orderBy('createdAt')), (snap) => setMerchandise(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubPolls = onSnapshot(query(collection(eventDocRef, 'polls'), orderBy('createdAt', 'desc')), (snap) => setPolls(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    
    // Fetch User's Registration (for the Ticket/QR Code)
    const checkUserRegistration = async () => {
        if (!userData?.uid) return;
        const regRef = collection(db, `families/${familyData.id}/events/${eventId}/registrations`);
        const q = query(regRef, where("registeredBy", "==", userData.uid));
        const snap = await getDocs(q);
        if (!snap.empty) { setUserRegistration({ id: snap.docs[0].id, ...snap.docs[0].data() }); }
    };
    checkUserRegistration();

    return () => { unsubEvent(); unsubItinerary(); unsubMerch(); unsubPolls(); };
  }, [familyData, eventId, userData]);

  const handleDeleteItinerary = async () => {
    try { await deleteDoc(doc(db, `families/${familyData.id}/events/${eventId}/itinerary/${modal.data.id}`)); toast.success('Itinerary item deleted.'); setModal({ type: null, data: null }); } 
    catch (error) { toast.error("Failed to delete itinerary item."); }
  };

  const handleDeleteMerch = async () => {
    try { await deleteDoc(doc(db, `families/${familyData.id}/events/${eventId}/merchandise/${modal.data.id}`)); toast.success('Merchandise deleted.'); setModal({ type: null, data: null }); } 
    catch (error) { toast.error("Failed to delete merchandise."); }
  };

  if (loading) { return <Layout><div className="flex justify-center items-center py-20"><Spinner size="lg" /></div></Layout>; }
  if (!event) { return <Layout><div className="text-center py-10"><h1 className="text-2xl font-bold">Event Not Found</h1></div></Layout>; }

  return (
    <Layout>
      {/* Modals - Note we pass canManage for permissions */}
      {modal.type === 'addItinerary' && <AddItineraryModal familyId={familyData.id} eventId={eventId} onClose={() => setModal({type: null})} />}
      {modal.type === 'editItinerary' && <AddItineraryModal familyId={familyData.id} eventId={eventId} existingItinerary={modal.data} onClose={() => setModal({type: null})} />}
      {modal.type === 'confirmDeleteItinerary' && <ConfirmationModal title="Delete Itinerary Item?" message={`Are you sure you want to delete "${modal.data.title}"?`} onConfirm={handleDeleteItinerary} onCancel={() => setModal({type: null})} />}
      
      {modal.type === 'addMerch' && canManage && <ManageMerchModal familyId={familyData.id} eventId={eventId} onClose={() => setModal({type: null})} />}
      {modal.type === 'editMerch' && canManage && <ManageMerchModal familyId={familyData.id} eventId={eventId} existingMerch={modal.data} onClose={() => setModal({type: null})} />}
      {modal.type === 'confirmDeleteMerch' && <ConfirmationModal title="Delete Merchandise?" message={`Are you sure you want to delete "${modal.data.itemName}"?`} onConfirm={handleDeleteMerch} onCancel={() => setModal({type: null})} />}
      
      {modal.type === 'createPoll' && canManage && <CreatePollModal familyId={familyData.id} eventId={eventId} onClose={() => setModal({type: null})} />}

      <div className="space-y-8">
        {/* Event Header Section */}
         <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
            <h1 className="text-4xl font-bold text-primary">{event.eventName}</h1>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-gray-600 mt-4">
                <p className="flex items-center gap-2"><FaCalendarAlt /><strong>Dates:</strong> {format(event.startDate.toDate(), 'MMM d')} - {format(event.endDate.toDate(), 'd, yyyy')}</p>
                <p className="flex items-center gap-2"><FaMapMarkerAlt /><strong>Location:</strong> {event.location}</p>
            </div>
            <div className="mt-6 border-t pt-4"><p className="text-gray-700 text-lg">{event.description}</p></div>

            {event.additionalInfo && (
                <div className="mt-6 bg-blue-50 border border-blue-100 p-4 rounded-md text-blue-900">
                    <h3 className="font-bold flex items-center gap-2 mb-2"><FaInfoCircle className="text-primary"/> Additional Information</h3>
                    <p className="text-sm whitespace-pre-wrap">{event.additionalInfo}</p>
                </div>
            )}

            {/* Display Registration Button OR Digital Ticket */}
            {!userRegistration ? (
                <div className="mt-6">
                    <Link to={`/event/${eventId}/register`} className="inline-block bg-cta-orange text-white font-bold py-3 px-8 rounded-lg shadow hover:bg-orange-700 transition-transform transform hover:scale-105">Register Now</Link>
                </div>
            ) : (
                <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6 flex flex-col md:flex-row items-center gap-8 shadow-inner">
                    <div className="bg-white p-3 rounded-lg shadow-sm border">
                        <QRCode value={userRegistration.id} size={150} level="H" />
                    </div>
                    <div className="text-center md:text-left">
                        <h3 className="text-2xl font-bold text-neutral-gray flex items-center justify-center md:justify-start gap-2"><FaTicketAlt className="text-primary"/> Your Digital Ticket</h3>
                        <p className="text-gray-600 mt-2">You are registered with <strong>{userRegistration.attendees.length} attendees</strong>.</p>
                        <p className="text-sm text-gray-500 mt-1">Have this QR code ready to scan at check-in.</p>
                        <p className="text-xs font-mono bg-gray-200 inline-block px-2 py-1 rounded mt-3">ID: {userRegistration.id}</p>
                    </div>
                </div>
            )}
        </div>

        {/* Photo Gallery */}
        <PhotoGallery storagePath={eventGalleryPath} title={`${event.eventName} Photos`} emptyMessage={`No photos from ${event.eventName} yet.`} />

        {/* Polls Section */}
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
            <div className="flex justify-between items-center mb-4"><h2 className="text-2xl font-semibold text-neutral-gray">Family Polls</h2>{canManage && <button onClick={() => setModal({type: 'createPoll'})} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-700 text-sm font-medium">Create Poll</button>}</div>
            {polls.length > 0 ? (<div className="space-y-6">{polls.map(p => <Poll key={p.id} poll={p} familyId={familyData.id} eventId={eventId} />)}</div>) : (<div className="text-center py-8"><FaPoll className="mx-auto text-4xl text-gray-300"/><p className="mt-4 text-gray-500">No polls have been created for this event yet.</p></div>)}
        </div>

        {/* Merchandise Section */}
        {(canManage || merchandise.length > 0) && (
          <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold text-neutral-gray">Merchandise</h2>
                {canManage && (<button onClick={() => setModal({type: 'addMerch'})} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-700 text-sm font-medium">Add Merchandise</button>)}
            </div>
            <div className="space-y-4">
              {merchandise.length > 0 ? ( merchandise.map(item => (
                <div key={item.id} className="p-4 border rounded-md flex justify-between items-start group transition-colors hover:bg-gray-50">
                    <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2"><FaTshirt/> {item.itemName} - ${item.basePrice.toFixed(2)}</h3>
                        <p className="text-sm text-gray-600 mt-1">Available Sizes: {item.sizes.map(s => `${s.size}${s.upcharge > 0 ? ` (+$${s.upcharge.toFixed(2)})` : ''}`).join(', ')}</p>
                    </div>
                    {canManage && (
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => setModal({ type: 'editMerch', data: item })} className="p-2 text-gray-500 hover:text-primary bg-white rounded shadow-sm border border-gray-200"><FaPencilAlt size={14}/></button>
                            <button onClick={() => setModal({ type: 'confirmDeleteMerch', data: item })} className="p-2 text-gray-500 hover:text-red-500 bg-white rounded shadow-sm border border-gray-200"><FaTrash size={14}/></button>
                        </div>
                    )}
                </div>
              )) ) : (<div className="text-center py-8"><FaGift className="mx-auto text-4xl text-gray-300"/><p className="mt-4 text-gray-500">No merchandise has been added for this event yet.</p></div>)}
            </div>
          </div>
        )}

        {/* Itinerary Section */}
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          <div className="flex justify-between items-center mb-4"><h2 className="text-2xl font-semibold text-neutral-gray">Reunion Itinerary</h2>{canManage && <button onClick={() => setModal({type: 'addItinerary'})} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-700 text-sm font-medium">Add Itinerary Item</button>}</div>
          <div className="space-y-6">
            {itinerary.length > 0 ? ( itinerary.map(item => (
              <div key={item.id} className="flex flex-col md:flex-row gap-4 border-l-4 border-primary pl-4 py-2 group relative transition-colors hover:bg-gray-50">
                <div className="w-full md:w-1/4">
                  <p className="font-bold text-lg">{format(item.date.toDate(), 'eeee, MMM d')}</p>
                  <p className="text-gray-500 flex items-center gap-2"><FaClock size={14}/> {item.time}</p>
                </div>
                <div className="w-full md:w-3/4 pr-16">
                  <h3 className="font-semibold text-xl text-neutral-gray">{item.title}</h3>
                  <p className="font-semibold text-gray-600">{item.locationName}</p>
                  <p className="text-sm text-gray-500">{item.address}</p>
                  {item.description && <p className="mt-2 text-gray-700">{item.description}</p>}
                </div>
                {canManage && (
                  <div className="absolute top-2 right-2 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white p-1 rounded-md shadow-sm border border-gray-200">
                      <button onClick={() => setModal({ type: 'editItinerary', data: item })} className="p-1.5 text-gray-500 hover:text-primary"><FaPencilAlt size={14}/></button>
                      <button onClick={() => setModal({ type: 'confirmDeleteItinerary', data: item })} className="p-1.5 text-gray-500 hover:text-red-500"><FaTrash size={14}/></button>
                  </div>
                )}
              </div>
            )) ) : (<div className="text-center py-8"><FaClipboardList className="mx-auto text-4xl text-gray-300"/><p className="mt-4 text-gray-500">The event itinerary has not been set up yet.</p></div>)}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EventDetailPage;